import { Alert, Button, Checkbox, Dialog, DialogContent, FormControlLabel, FormGroup, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getCloudOptimizedSlidesStatus, runCloudOptimizedSlides } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import React, { FunctionComponent, useMemo, useState } from 'react';
import { BooleanParam, StringParam, useQueryParams } from 'use-query-params';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { CasesParams } from 'utils/useCasesParams/types';
import { usePermissions } from 'utils/usePermissions';
import { filter, groupBy, map, size, sortBy, uniq } from 'lodash';
import { Permission } from 'interfaces/permissionOption';
import {
  DialogChromeFooter,
  DialogChromeHeader,
  dialogActionButtonSx,
  dialogPrimaryActionSx,
} from 'components/atoms/Dialog/DialogChrome';
import { JobsTab, useStudyDashboardTabState } from 'components/StudyDashboard/useStudyDashboardTabState';

const WAITING_SNACKBAR_KEY = 'RUN_CLOUD_OPTIMIZED_SLIDES';
const UNKNOWN_VERSION_KEY = '__unknown_cloud_optimization_version__';

interface ExistingVersionGroup {
  versionKey: string;
  versionLabel: string;
  slideIds: string[];
}

const formatSlideCount = (slideCount: number) => `${slideCount} slide${slideCount === 1 ? '' : 's'}`;

const getVersionKey = (version: string | null | undefined) => version || UNKNOWN_VERSION_KEY;

const getVersionLabel = (versionKey: string) => (versionKey === UNKNOWN_VERSION_KEY ? 'unknown version' : versionKey);

export interface RunCloudOptimizedSlidesJobProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
  platformVersionWarning?: React.ReactNode;
}

export const RunCloudOptimizedSlidesJob: FunctionComponent<RunCloudOptimizedSlidesJobProps> = ({
  onClose,
  casesParams,
  platformVersionWarning,
}) => {
  const [open, setOpen] = useState(true);
  const [includedExistingVersions, setIncludedExistingVersions] = useState<Record<string, boolean>>({});
  const [forceRecreate, setForceRecreate] = useState(false);
  const { hasPermission } = usePermissions();
  const canViewJobs = hasPermission(Permission.ViewJobs);
  const [, setStudyDashboardTab] = useStudyDashboardTabState();
  const [, setJobDrawerQuery] = useQueryParams({
    jobDrawerOpen: BooleanParam,
    currentJobId: StringParam,
  });

  const studyId = casesParams.filters?.studyId || casesParams.studyId;
  const hasExplicitSlideSelection = size(casesParams.slideIdsToInclude) > 0;

  const slidesQuery = useQuery({
    queryKey: ['cloudOptimizedSlidesSelectionSlides', casesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(casesParams, signal),
    enabled: Boolean(studyId) && !hasExplicitSlideSelection,
  });

  const resolvedSelectionManifest = useMemo(() => {
    if (hasExplicitSlideSelection) {
      return uniq(casesParams.slideIdsToInclude || []);
    }

    return uniq(map(slidesQuery.data || [], 'slideId'));
  }, [casesParams.slideIdsToInclude, hasExplicitSlideSelection, slidesQuery.data]);

  const cloudOptimizedStatusQuery = useQuery({
    queryKey: ['cloudOptimizedSlidesStatus', studyId, resolvedSelectionManifest],
    queryFn: ({ signal }) =>
      getCloudOptimizedSlidesStatus(
        {
          ...casesParams,
          configParams: {
            slideIds: resolvedSelectionManifest,
          },
        },
        signal
      ),
    enabled: Boolean(studyId) && size(resolvedSelectionManifest) > 0,
    retry: false,
  });

  const existingVersionGroups = useMemo<ExistingVersionGroup[]>(() => {
    const slidesWithExistingResults = filter(
      cloudOptimizedStatusQuery.data?.slides || [],
      (slideStatus) => slideStatus.hasCloudOptimizedSlides
    );
    const groupedSlides = groupBy(slidesWithExistingResults, (slideStatus) =>
      getVersionKey(slideStatus.cloudOptimizationVersion)
    );

    return sortBy(
      map(groupedSlides, (slides, versionKey) => ({
        versionKey,
        versionLabel: getVersionLabel(versionKey),
        slideIds: map(slides, 'slideId'),
      })),
      'versionLabel'
    );
  }, [cloudOptimizedStatusQuery.data]);

  const missingSlideIds = useMemo(
    () => uniq(cloudOptimizedStatusQuery.data?.missingSlideIds || []),
    [cloudOptimizedStatusQuery.data]
  );

  const includedExistingSlideIds = useMemo(
    () =>
      uniq(
        map(
          filter(existingVersionGroups, (group) => includedExistingVersions[group.versionKey] ?? true),
          'slideIds'
        ).flat()
      ),
    [existingVersionGroups, includedExistingVersions]
  );

  const manifest = useMemo(() => {
    if (cloudOptimizedStatusQuery.isError || !cloudOptimizedStatusQuery.data) {
      return resolvedSelectionManifest;
    }

    const includedSlideIds = new Set([...missingSlideIds, ...includedExistingSlideIds]);

    return filter(resolvedSelectionManifest, (slideId) => includedSlideIds.has(slideId));
  }, [
    cloudOptimizedStatusQuery.data,
    cloudOptimizedStatusQuery.isError,
    includedExistingSlideIds,
    missingSlideIds,
    resolvedSelectionManifest,
  ]);

  const existingSlideCount = useMemo(
    () => size(map(existingVersionGroups, 'slideIds').flat()),
    [existingVersionGroups]
  );

  const hasIncludedExistingSlides = size(includedExistingSlideIds) > 0;
  const excludedExistingSlideCount = existingSlideCount - size(includedExistingSlideIds);
  const shouldForceRecreate = forceRecreate && hasIncludedExistingSlides;

  const cloudOptimizedSlidesMutation = useMutationWithErrorSnackbar({
    mutationFn: runCloudOptimizedSlides,
    mutationDescription: 'launch cloud optimized slides',
    onSuccess: ({ job_id }) => {
      enqueueSnackbar(`Cloud optimized slides started. Job ID: ${job_id}`, { variant: 'success' });

      if (canViewJobs) {
        setStudyDashboardTab(JobsTab);
        setJobDrawerQuery({ jobDrawerOpen: true, currentJobId: job_id }, 'replaceIn');
      }
    },
  });

  const handleClose = () => {
    setOpen(false);
    onClose(undefined, true);
  };

  const handleExecute = async () => {
    if (!studyId || size(manifest) === 0) {
      return;
    }

    enqueueSnackbar('Waiting for cloud optimized slides to start', {
      key: WAITING_SNACKBAR_KEY,
      autoHideDuration: null,
      variant: 'success',
    });

    try {
      await cloudOptimizedSlidesMutation.mutateAsync({
        studyId,
        configParams: {
          manifest,
          params: shouldForceRecreate ? { forceRecreate: true } : {},
        },
      });
      handleClose();
    } finally {
      closeSnackbar(WAITING_SNACKBAR_KEY);
    }
  };

  const isResolvingManifest = slidesQuery.isLoading && !hasExplicitSlideSelection;
  const isCheckingExistingResults =
    size(resolvedSelectionManifest) > 0 &&
    !cloudOptimizedStatusQuery.isError &&
    (cloudOptimizedStatusQuery.isLoading || cloudOptimizedStatusQuery.isFetching);
  const isExecuteDisabled =
    !studyId ||
    isResolvingManifest ||
    isCheckingExistingResults ||
    size(manifest) === 0 ||
    cloudOptimizedSlidesMutation.isLoading;

  const handleVersionSelectionChange = (versionKey: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setIncludedExistingVersions((currentSelection) => ({
      ...currentSelection,
      [versionKey]: event.target.checked,
    }));
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogChromeHeader
        title="Generate Cloud Optimized Slides"
        subtitle="Launch cloud-optimized slide generation for the current selection."
        onClose={handleClose}
        sticky
      />
      <DialogContent dividers>
        {platformVersionWarning}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {isResolvingManifest
            ? 'Resolving the selected slides...'
            : `Resolved ${formatSlideCount(size(resolvedSelectionManifest))} from the current selection.`}
        </Typography>
        {!isResolvingManifest &&
          size(resolvedSelectionManifest) > 0 &&
          !isCheckingExistingResults &&
          !cloudOptimizedStatusQuery.isError && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {existingSlideCount === 0
                ? 'No selected slides currently have cloud-optimized results.'
                : `${formatSlideCount(existingSlideCount)} already have cloud-optimized results across ${size(
                    existingVersionGroups
                  )} version${size(existingVersionGroups) === 1 ? '' : 's'}.`}
            </Typography>
          )}
        {!isResolvingManifest && size(resolvedSelectionManifest) > 0 && (
          <Typography variant="body2" color="text.secondary">
            {isCheckingExistingResults
              ? 'Checking existing cloud-optimized slide results...'
              : `This run will submit ${formatSlideCount(size(manifest))} to the backend launch route.`}
          </Typography>
        )}
        {cloudOptimizedStatusQuery.isError && !isResolvingManifest && size(resolvedSelectionManifest) > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Could not check existing cloud-optimized results. The current selection will be submitted unchanged.
          </Alert>
        )}
        {!isCheckingExistingResults && size(existingVersionGroups) > 0 && !cloudOptimizedStatusQuery.isError && (
          <Alert severity="info" sx={{ mt: 2, alignItems: 'flex-start' }}>
            <Typography sx={{ fontWeight: 500 }}>Existing cloud-optimized results were found.</Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              Uncheck a version to remove those slides from this run, or turn on force regenerate to recreate included
              existing results.
            </Typography>
            <FormGroup sx={{ mt: 1.5 }}>
              {map(existingVersionGroups, (group) => {
                const slideCount = size(group.slideIds);

                return (
                  <FormControlLabel
                    key={group.versionKey}
                    control={
                      <Checkbox
                        checked={includedExistingVersions[group.versionKey] ?? true}
                        onChange={handleVersionSelectionChange(group.versionKey)}
                      />
                    }
                    label={`Include ${formatSlideCount(slideCount)} with existing version ${group.versionLabel}`}
                  />
                );
              })}
            </FormGroup>
            <FormControlLabel
              sx={{ mt: 0.5 }}
              control={
                <Checkbox
                  checked={shouldForceRecreate}
                  disabled={!hasIncludedExistingSlides}
                  onChange={(event) => setForceRecreate(event.target.checked)}
                />
              }
              label="Force regenerate included slides with existing cloud-optimized results"
            />
            <Typography variant="body2" color="text.secondary">
              Without force regenerate, the algo keeps existing results for included slides.
            </Typography>
            {excludedExistingSlideCount > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Excluding {formatSlideCount(excludedExistingSlideCount)} with pre-existing cloud-optimized results.
              </Typography>
            )}
          </Alert>
        )}
        {!isResolvingManifest && size(manifest) === 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {size(resolvedSelectionManifest) === 0
              ? 'No slides were resolved from the current selection.'
              : 'No slides remain selected for this run.'}
          </Alert>
        )}
      </DialogContent>
      <DialogChromeFooter sticky>
        <Button onClick={handleClose} color="secondary" sx={dialogActionButtonSx}>
          Cancel
        </Button>
        <Button
          onClick={handleExecute}
          color="primary"
          variant="contained"
          disabled={isExecuteDisabled}
          sx={dialogPrimaryActionSx}
        >
          Execute
        </Button>
      </DialogChromeFooter>
    </Dialog>
  );
};

export default RunCloudOptimizedSlidesJob;
